import React from 'react';
import { Mail, Phone, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DOBPicker } from '../ui/dob-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// One field style for every booking form so inputs, selects and the DOB picker line up at the same height.
export const FIELD_CLS = 'h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6600]/30 focus-visible:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/30 focus:border-[#FF6600] data-[state=open]:border-[#FF6600] transition-colors';

export const TITLES_BY_TYPE = { ADULT: ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr'], YOUTH: ['Mr', 'Ms', 'Miss'], CHILD: ['Mstr', 'Miss'], INFANT: ['Mstr', 'Miss'] };
const TYPE_META = {
  ADULT: { label: 'Adult', age: '16+ years' },
  YOUTH: { label: 'Youth', age: '12–15 years' },
  CHILD: { label: 'Child', age: '2–11 years' },
  INFANT: { label: 'Infant', age: 'Under 2 years' },
};

export const Field = ({ label, required, hint, icon: Icon, children, className = '', htmlFor }) => (
  <div className={`min-w-0 ${className}`}>
    <Label htmlFor={htmlFor} className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
      {Icon && <Icon className="h-3.5 w-3.5 text-[#FF6600]" />}
      <span>{label}{required && <span className="text-[#FF6600] ml-0.5">*</span>}</span>
    </Label>
    {children}
    {hint && <p className="mt-1 text-[11px] leading-snug text-slate-500">{hint}</p>}
  </div>
);

export const PassengerFields = ({ form, update, titles, passengerType = 'ADULT', docsRequired = true, testIdPrefix, ids = {}, minExpiry }) => {
  const type = (passengerType || 'ADULT').toUpperCase();
  const titleOptions = titles || TITLES_BY_TYPE[type] || TITLES_BY_TYPE.ADULT;
  const id = { title: `${testIdPrefix}-title`, first: `${testIdPrefix}-first-name`, last: `${testIdPrefix}-last-name`, dob: `${testIdPrefix}-dob`, passport: `${testIdPrefix}-passport-number`, expiry: `${testIdPrefix}-passport-expiry`, ...ids };
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-[130px_1fr_1fr]">
        <Field label="Title" required htmlFor={id.title}>
          <Select value={form.title || ''} onValueChange={(v) => update('title', v)}>
            <SelectTrigger id={id.title} className={FIELD_CLS} data-testid={id.title}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{titleOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="First name" required htmlFor={id.first}>
          <Input id={id.first} className={FIELD_CLS} value={form.first_name || ''} onChange={(e) => update('first_name', e.target.value)} placeholder="Given name(s) as on passport" autoComplete="given-name" data-testid={id.first} />
        </Field>
        <Field label="Last name" required htmlFor={id.last}>
          <Input id={id.last} className={FIELD_CLS} value={form.last_name || ''} onChange={(e) => update('last_name', e.target.value)} placeholder="Surname as on passport" autoComplete="family-name" data-testid={id.last} />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Date of birth" required hint={TYPE_META[type]?.age}>
          <DOBPicker className={FIELD_CLS} value={form.date_of_birth || ''} onChange={(d) => update('date_of_birth', d)} passengerType={type} placeholder="Select date of birth" data-testid={id.dob} />
        </Field>
        <Field label="Passport number" required={docsRequired} htmlFor={id.passport} hint={docsRequired ? undefined : 'Optional for this airline'}>
          <Input id={id.passport} className={`${FIELD_CLS} font-mono tracking-wider uppercase placeholder:normal-case placeholder:tracking-normal placeholder:font-sans`} value={form.passport_number || ''} onChange={(e) => update('passport_number', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15))} placeholder="e.g. 123456789" autoComplete="off" data-testid={id.passport} />
        </Field>
        <Field label="Passport expiry" required={docsRequired} htmlFor={id.expiry} hint="6+ months validity needed on travel date">
          <Input id={id.expiry} type="date" min={minExpiry} className={`${FIELD_CLS} [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer`} value={form.passport_expiry || ''} onChange={(e) => update('passport_expiry', e.target.value)} data-testid={id.expiry} />
        </Field>
      </div>
    </div>
  );
};

export const ContactFields = ({ email, phone, onEmail, onPhone, testIdPrefix = 'contact', emailId, phoneId, className = '', heading = 'Contact details', subtitle = 'Your e-ticket and booking confirmation are sent here' }) => (
  <div className={`space-y-3 ${className}`}>
    {heading && (
      <div>
        <div className="font-bold text-slate-900 text-sm">{heading}</div>
        {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
      </div>
    )}
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Email address" required icon={Mail} htmlFor={emailId || `${testIdPrefix}-email`}>
        <Input id={emailId || `${testIdPrefix}-email`} type="email" inputMode="email" className={FIELD_CLS} value={email} onChange={(e) => onEmail(e.target.value)} placeholder="name@example.com" autoComplete="email" data-testid={`${testIdPrefix}-email`} />
      </Field>
      <Field label="Phone number" required icon={Phone} htmlFor={phoneId || `${testIdPrefix}-phone`}>
        <Input id={phoneId || `${testIdPrefix}-phone`} type="tel" inputMode="tel" className={FIELD_CLS} value={phone} onChange={(e) => onPhone(e.target.value)} placeholder="+44 7XXX XXXXXX" autoComplete="tel" data-testid={`${testIdPrefix}-phone`} />
      </Field>
    </div>
  </div>
);

export const PassengerHeading = ({ index, total, type = 'ADULT', lead = false, label, testIdPrefix = `passenger-${index}` }) => {
  const meta = TYPE_META[(type || 'ADULT').toUpperCase()] || TYPE_META.ADULT;
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4">
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF6600] text-white text-sm font-bold shadow-sm">{index + 1}</span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 leading-tight">
            <span className="font-bold text-slate-900">{label || meta.label}</span>
            {lead && <span className="rounded-full bg-orange-50 border border-orange-200 px-2 py-0.5 text-[11px] font-semibold text-[#FF6600]" data-testid={`${testIdPrefix}-lead-badge`}>Lead passenger</span>}
          </div>
          <div className="text-xs text-slate-500">Passenger {index + 1} of {total} · {meta.age}</div>
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-500 shrink-0"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />Names exactly as on passport</div>
    </div>
  );
};

export const PassengerCard = ({ index, total, type, lead, label, testId, children }) => (
  <Card className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
    <CardContent className="p-4 sm:p-6 space-y-5">
      <PassengerHeading index={index} total={total} type={type} lead={lead} label={label} testIdPrefix={testId} />
      {children}
    </CardContent>
  </Card>
);
